import { useCallback, useMemo, useRef, useState } from 'react';

import { useForm } from 'react-hook-form';
import { useQuery } from 'react-query';
import { Cell, Row } from 'react-table';

import { TBaseResponse, TFilterResponse } from 'src/types/Commons/filter.model';
import useLanguage from '../Language/useLanguage';
import { TProductFilterRequest, TProductItem } from 'src/types/Products/product.model';
import { getProducts } from 'src/apis/Products/product';
import useUserProfile from '../Users/useUserProfile';
import { getFilterFromURL } from 'src/utils/commons/url';
import Action from 'src/components/Commons/Table/action';

function useProductTable() {
  const headerRef = useRef(null);
  //#region State
  const fetchIdRef = useRef(0);
  const { language } = useLanguage();
  const [filters, setFilters] = useState(getFilterFromURL(location?.search));
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [param, setParam] = useState<TProductFilterRequest>({
    page: 1,
    take: 10
  });
  const { profile } = useUserProfile();
  const [id, setId] = useState<number | undefined>();
  //#endregion

  //#region API
  const { data: response, isFetching } = useQuery<TBaseResponse<TFilterResponse<TProductItem>>>(
    ['getProducts', param],
    async () => getProducts(param),
    {
      enabled: !!param,
      keepPreviousData: true
    }
  );
  //#endregion

  //#region hook form
  const { register, handleSubmit, control, reset, setValue } = useForm<TProductFilterRequest>({
    mode: 'onSubmit',
    defaultValues: {}
  });
  //#endregion

  //#region Columns
  const columns = useMemo(
    () => [
      {
        Header: () => {
          return <div className="flex justify-start"></div>;
        },
        accessor: 'id',
        sortable: false,
        displayFilter: false,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        Cell: ({ row, cell }: { row: Row<TProductItem>; cell: Cell<TProductItem> }) => {
          return <div className="cursor-pointer font-semibold">{cell.value}</div>;
        }
      },
      {
        Header: 'Tên',
        accessor: 'name',
        sortable: false,
        displayFilter: false,
        width: 400
      },
      {
        Header: 'Mã',
        accessor: 'code',
        sortable: false,
        displayFilter: false,
        width: 400
      },
      {
        Header: 'Giá nhập',
        accessor: 'importPrice',
        sortable: true,
        width: 150,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        Cell: ({ row, cell }: { row: Row<TProductItem>; cell: Cell<TProductItem> }) => {
          return <div className="text-right">{cell.value}</div>;
        }
      },
      {
        Header: 'Giá bán',
        accessor: 'salePrice',
        displayFilter: false,
        sortable: false,
        width: 150,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        Cell: ({ row, cell }: { row: Row<TProductItem>; cell: Cell<TProductItem> }) => {
          return <div className="text-right">{cell.value}</div>;
        }
      },

      {
        Header: () => {
          return <div className="flex justify-end">{language.profilePage.usersTablePage.columnAction}</div>;
        },
        id: 'action',
        sortable: false,
        Cell: ({ row }: { row: Row<TProductItem> }) => {
          return <Action onView={id => setId(id)} id={row.original.id || 0} />;
        }
      }
    ],

    []
  );
  //#endregion

  //#region Events

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
  };
  const handleFetchData = useCallback((fetchParams: any) => {
    // eslint-disable-next-line
    const fetchId = ++fetchIdRef.current;

    if (fetchId === fetchIdRef.current) {
      fetchParams.userId = profile.userId;
      setParam(fetchParams);
    }
  }, []);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars

  const onSubmit = (data: TProductFilterRequest) => {
    setParam(data);
  };

  const handleReset = () => {
    reset();
  };

  //#endregion

  return {
    profile,

    location,
    history,

    isDrawerOpen,
    setIsDrawerOpen,
    param,
    setParam,
    response,
    isFetching,
    handleCloseDrawer,
    columns,
    onSubmit,
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    filters,
    setFilters,
    handleFetchData,
    handleReset,
    headerRef,
    id,
    setId
  };
}

export default useProductTable;
